import { ReactNode, useMemo } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";

import AlertTriangleIcon from "@assets/icons/alert-icon.svg";
import StarsUploaderIcon from "@assets/icons/stars.svg";

import { IEmailAttachment } from "@interfaces/email.interface";
import { getErrorMessagesForDocuments } from "@utils/files.util";
import { UploaderAttachmentsList } from "./UploaderAttachmentsList";
import { UploadedDocumentsList } from "./UploadedDocumentsList";
import { ICreateDealForm } from "../AddNewDealDialog";
import { UploaderInput } from "./UploaderInput";
import { UploaderDropzone } from "@ui/Uploader";
import { UploaderErrors } from "@ui/Uploader";
import { View } from "@ui/View";

interface ICIMUploaderProps {
  control?: Control<ICreateDealForm>;
  setValue?: UseFormSetValue<ICreateDealForm>;
  attachments: IEmailAttachment[];
  apiError?: ReactNode | null;
}

export function CIMUploader({ control, setValue, attachments, apiError }: ICIMUploaderProps) {
  const selectedDocuments = useWatch({ control }).documents || [];
  const selectedAttachments = useWatch({ control }).attachments || [];
  const selectedAttachmentId = selectedAttachments[0]?.document?.id || "";

  const handleAddDocuments = async (documents: File[]) => {
    const updatedDocuments = [...selectedDocuments, ...documents];

    setValue?.("documents", updatedDocuments, { shouldDirty: true, shouldValidate: true });
  };

  const handleRemoveDocument = (index: number) => {
    if (!selectedDocuments) return;

    const updatedDocuments = selectedDocuments.filter((_, i) => i !== index);
    setValue?.("documents", updatedDocuments, { shouldDirty: true, shouldValidate: true });
  };

  const handleToogleAttachment = (attachment: IEmailAttachment) => {
    setValue?.("attachments", [attachment], { shouldDirty: true });
  };

  const showAttachments = !!attachments?.length && !selectedDocuments?.length;
  const errorMessages = useMemo(() => {
    return getErrorMessagesForDocuments(selectedDocuments);
  }, [selectedDocuments]);

  return (
    <div className="relative p-3 flex flex-col gap-2 bg-teal-50 dark:bg-gray-700 rounded-lg overflow-hidden">
      {/* Background */}
      <div
        className="absolute z-0 inset-0 rounded-xl pointer-events-none"
        style={{
          background: `linear-gradient(to right, rgba(112,239,239,0.3) 10%, rgba(70,206,181,0.15) 30%, transparent 100%)`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 60%",
          backgroundPosition: "top left",
          filter: "blur(50px)",
          opacity: 0.7,
        }}
      />
      <StarsUploaderIcon className="z-0 absolute top-0 right-0" />

      {/* Dropzone */}
      <UploaderDropzone onFilesSelected={handleAddDocuments} />
      {/* Uploader */}
      <View.Condition if={selectedDocuments.length === 0}>
        <UploaderInput onFilesSelected={handleAddDocuments} />
      </View.Condition>
      {/* Uploded documents list */}
      <View.Condition if={selectedDocuments.length > 0}>
        <UploadedDocumentsList documents={selectedDocuments} onRemove={handleRemoveDocument} />
      </View.Condition>
      <UploaderErrors errorMessages={errorMessages} />
      {apiError && (
        <div className="relative mt-1 flex items-center gap-3 rounded-lg border border-stroke-primary bg-red-50 p-2">
          <AlertTriangleIcon width={20} height={20} className="flex-shrink-0 text-red-600" />
          <span className="text-sm text-content-error">{apiError}</span>
        </div>
      )}
      {/* Attachments list */}
      <View.Condition if={showAttachments}>
        <UploaderAttachmentsList
          attachments={attachments}
          selectedAttachmentId={selectedAttachmentId}
          onSelectAttachment={handleToogleAttachment}
        />
      </View.Condition>
    </div>
  );
}
